import React, { useEffect, useRef, useState } from "react";
import { redirect } from "react-router-dom";

// Déclarer window.Yousign pour TypeScript
declare global {
    interface Window {
        Yousign: any;
    }
}

// Props du composant
interface YousignIframeProps {
    external_id: string;
    external_type: string;
}

// Fonction pour récupérer le lien de signature
const fetchSignatureLink = async (
    external_id: string,
    external_type: string
): Promise<string> => {
    const token = localStorage.getItem("token");
    if (!token) {
        throw new Error("Token non trouvé, impossible de charger le lien de signature");
    }

    const res = await fetch(
        `${process.env.REACT_APP_API_URL}/documents/signature_link`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ external_id, external_type }),
        }
    );

    const data = await res.json();

    if (!res.ok) {
        // Si l'API renvoie un message d'erreur
        throw new Error(data.message || "Erreur inconnue lors de la récupération du lien de signature");
    }

    return data.signature_link;
};

const YousignIframe: React.FC<YousignIframeProps> = ({
    external_id,
    external_type,
}) => {
    const iframeContainerRef = useRef<HTMLDivElement | null>(null);
    const listenerRef = useRef<{ removeMessageListener: () => void } | null>(null);

    const [signatureLink, setSignatureLink] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);

        fetchSignatureLink(external_id, external_type)
            .then((link) => {
                setSignatureLink(link);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setError(err.message);
                setLoading(false);
            });
    }, [external_id, external_type]);

    useEffect(() => {
        if (!signatureLink || !iframeContainerRef.current) return;

        // Nettoyage du conteneur
        iframeContainerRef.current.innerHTML = "";

        // Création de l'iframe
        const iframe = document.createElement("iframe");
        iframe.src = signatureLink;
        iframe.width = "100%";
        iframe.style.height = "100vh";
        iframe.style.border = "none";
        iframe.style.overflow = "auto";

        iframeContainerRef.current.appendChild(iframe);

        return () => {
            // Nettoyer le conteneur
            if (iframeContainerRef.current) {
                iframeContainerRef.current.innerHTML = "";
            }
            // Nettoyer le listener
            if (listenerRef.current) {
                listenerRef.current.removeMessageListener();
                listenerRef.current = null;
            }
        };
    }, [signatureLink]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (!event.data) return;
            console.log(event.data);
            // Vérifie que le message vient bien de Yousign si possible
            if (event.data.event === "success") {
                console.log("Signature terminée", event.data);

                // Selon le rôle de l'user, tu peux redirect ou reload
                if (external_type === "operation") {
                    window.location.href = `/operations/${external_id}?tab=3`;
                } else if (external_type === "commande") {
                    window.location.href = `/commandes/${external_id}?tab=2`;
                } else {
                    window.location.reload();
                }
            }
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [external_id, external_type]);

    if (loading) return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '1.75rem' }}>
            <p>Chargement du document de signature...</p>
        </div>
    );
    if (error) return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', fontSize: '1.75rem' }}>
            <p>{error}</p>
            <p>
                Veuillez contacter le support.
            </p>
        </div>
    );
    return <div ref={iframeContainerRef}></div>;
};

export default YousignIframe;